/**
 * Full-page redirect for pages that exist only to send the user somewhere else.
 *
 * Safari keeps such a page in its back/forward cache, so pressing Back restores it exactly as
 * it was left: mid-redirect, spinner showing, with no effect due to run again. The returned
 * function records that this document has started leaving. If the document is later restored
 * from that cache, it reloads so the page resolves its destination afresh.
 *
 * Nothing happens on an ordinary load, or on a restore of a page that never started a redirect.
 *
 * Before leaving it sends a PostHog event naming the destination host. Session replays stop
 * recording once the browser leaves the app, so without it a hand-off to the intake form looks
 * like a spinner that never finished. A failure to send the event never blocks the redirect.
 */
import { useCallback, useEffect } from 'react';
import { analytics } from '@shared/posthog';

// Module scope rather than a ref: the flag describes the document, so it has to outlive a
// remount of whichever component began the redirect.
let redirectStarted = false;

function recordRedirect(url: string) {
  try {
    const page = new URL(document.baseURI);
    const destination = new URL(url, page);
    analytics.auth.fullPageRedirectStarted({
      destinationHost: destination.host,
      leavesApp: destination.origin !== page.origin,
    });
  } catch {
    // Analytics only; the redirect goes ahead regardless.
  }
}

export function useFullPageRedirect(): (url: string) => void {
  useEffect(() => {
    function reloadIfRestoredMidRedirect(event: PageTransitionEvent) {
      if (event.persisted && redirectStarted) {
        window.location.reload();
      }
    }

    window.addEventListener('pageshow', reloadIfRestoredMidRedirect);
    return () => window.removeEventListener('pageshow', reloadIfRestoredMidRedirect);
  }, []);

  return useCallback((url: string) => {
    redirectStarted = true;
    recordRedirect(url);
    window.location.href = url;
  }, []);
}
